'use client';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useMemo } from 'react';
import Image from '@repo/ui/src/components/Image';
import { buildStaticImage } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { Region } from '@repo/ui/src/types/LanguageSelector';

export interface Props {
  showLabel?: boolean;
  title?: string;
  regions: Array<Region>;
  className?: string;
}

const LanguageSelector = ({ showLabel = true, title = 'Select a Different Location', regions, className = "tw-flex tw-items-center tw-gap-3 tra-text-web-body-s sm:tw-text-white" }: Props) => {
  const { country } = useCurrentUrl();
  const { push } = useModal(ModalID.languageSelector);

  const { countryName, language, flagImage } = useMemo(() => {
    const locales = regions?.map(({ locales }) => locales).flat() || [];
    return (
      locales?.find((_locale) => `/${_locale?.link?.link}` === country || _locale?.link?.link === country) ??
      locales?.find((_locale) => _locale?.countryName === 'United States') ?? {
        countryName: '',
        language: '',
        flagImage: null,
      }
    );
  }, [regions, country]);

  const getImageSrc = () => {
    if (!flagImage) return '';
    
    if (flagImage?.defaultHost?.startsWith('/')) {
      return `${flagImage.defaultHost}/${flagImage.id}.svg`;
    } else {
      return buildStaticImage(flagImage);
    }
  };

  return (
    <button
      className={className}
      onClick={() => push({ title, regions })}
      aria-label="open"
    >
      {flagImage && (
        <div className="tw-inline-block tw-overflow-hidden tw-relative tw-h-6 tw-w-6 tw-rounded-full">
          <Image src={getImageSrc()} alt={flagImage?.name} layout="fill" />
        </div>
      )}
      {showLabel && `${countryName || 'United States'} / ${language || 'English'}`}
    </button>
  );
};

export default LanguageSelector;